// ===========================================
// AUDIO SUPPRESSION — tab-visibility SFX guard (standalone)
// ===========================================
// Prevents the SFX burst when returning to a backgrounded autoplay tab.
// A backgrounded tab pauses rAF and throttles timers, so the animation/SFX
// pipeline stalls while autoplay keeps generating events; on return the whole
// backlog flushes at once. We suppress audio while hidden, and keep it
// suppressed through a short settle window after return so the flushing
// backlog is dropped — new events re-enable audio.
//
// This lives in its OWN module (not tableAudio.ts) on purpose: both
// tableAudio.ts and animationRunner.ts need the suppression flag, and
// tableAudio ⇄ animationRunner already form an import edge (BASE_VOLUMES).
// Putting the flag here avoids a circular import (which caused a TDZ crash:
// "Cannot access 'BASE_VOLUMES' before initialization").
// ===========================================

import { sound } from '@pixi/sound';

let audioSuppressed = false;
const SETTLE_WINDOW_MS = 350; // Suppress this long after the tab becomes visible

/**
 * True while the tab is hidden or within the post-return settle window.
 * All SFX entry points early-return on this.
 */
export function isAudioSuppressed(): boolean {
  return audioSuppressed;
}

// Register the visibility listener once at module load.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      // Tab hidden → suppress immediately and stop anything in flight.
      audioSuppressed = true;
      try {
        sound.pauseAll();
      } catch {
        // Ignore errors if nothing is playing
      }
    } else {
      // Tab visible → resume the audio context, but keep suppression active
      // through the settle window while the rAF backlog flushes.
      try {
        sound.resumeAll();
      } catch {
        // Ignore errors
      }
      setTimeout(() => {
        audioSuppressed = false;
      }, SETTLE_WINDOW_MS);
    }
  });
}
